/**
 * Copyright Highway9 Networks Inc.
 */

import { DependencyList, useCallback, useEffect, useRef } from "react";

type IntervalCallback = (isImmediateCall?: boolean) => void | Promise<void>;

/**
 * @param callback - The function to be called every interval
 * @param delay - The interval in milliseconds
 * @param immediate - Whether to call the function immediately
 * @param deps - The dependencies to be passed to useEffect
 * @returns ClearInterval - A function to clear the interval
 */
export function useInterval(
  callback: IntervalCallback,
  delay: number | null | false,
  immediate = false,
  deps: DependencyList = []
): { ClearInterval: () => void } {
  const savedCallback = useRef<IntervalCallback>(callback);
  // Refs, not render-scoped bindings: the chain re-arms itself from inside a
  // promise callback, so the handle has to outlive the render that scheduled it,
  // and the returned ClearInterval has to see the live handle (MCS-4316)
  const intervalIdRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const cancelledRef = useRef(false);

  /**
   * Cancels the current interval
   */
  const ClearInterval = useCallback((): void => {
    cancelledRef.current = true;
    if (intervalIdRef.current) {
      clearTimeout(intervalIdRef.current);
      intervalIdRef.current = undefined;
    }
  }, []);

  // Remember the latest callback.
  useEffect(() => {
    savedCallback.current = callback;
  });

  // Execute callback if immediate is set.
  // biome-ignore lint/correctness/useExhaustiveDependencies: preserve existing hook dependency list
  useEffect(() => {
    if (!immediate) return;
    if (delay === null || delay === false) return;
    void savedCallback.current(true);
  }, [immediate, ...deps]);

  // Set up the interval.
  useEffect(() => {
    if (delay === null || delay === false) return undefined;
    cancelledRef.current = false;
    const tick = async () => {
      await savedCallback.current();
    };

    // Alternate to the setInterval function but for performance reasons. It will call after first callback is done.
    intervalIdRef.current = setTimeout(function call() {
      try {
        // make sure the interval is called after execution of the callback function
        void tick().finally(() => {
          // Cleanup can run while the callback is still in flight; without this
          // the chain would re-arm a timer nothing holds a reference to
          if (cancelledRef.current) return;
          intervalIdRef.current = setTimeout(call, delay);
        });
      } catch (_error) {
        if (cancelledRef.current) return;
        intervalIdRef.current = setTimeout(() => {
          void tick();
        }, delay * 2);
      }
    }, delay);
    return () => ClearInterval();
  }, [delay, ClearInterval]);

  return { ClearInterval };
}

/**
 * refresh every n seconds for particular duration of time and then stop refreshing
 *
 * @param interval in milliseconds
 * @param duration in milliseconds
 * @param callback
 */
export function refreshEvery(interval: number, duration: number, callback: () => void): void {
  // const intervalId = setInterval(callback, interval);
  // setTimeout(() => clearInterval(intervalId), duration + 1000);

  const intervalID = setInterval(() => {
    callback();
    console.count(`refresh every ${interval / 1000} seconds`);
  }, 10000);
  setTimeout(() => {
    clearInterval(intervalID);
    console.log(`refresh every ${interval / 1000} seconds stopped`);
  }, duration + 1000);
}
